import { useCallback, useEffect, useState } from 'react';
import useThrowAsyncError from '../../../hooks/useThrowAsyncError';
import {
  deleteFavoriteWord,
  deleteFavoriteWords,
  getFavoriteWordsForModule,
  putFavoriteWord,
} from '../../../api/word-list';
import { WordlistContextValue, WordlistTab } from '../_contexts/WordlistContext';

export default function useWordlistFavorites({
  wordlist,
  currentTab,
  selectedWord,
  setSelectedWord,
  module,
}: Pick<WordlistContextValue, 'wordlist' | 'currentTab' | 'selectedWord' | 'setSelectedWord' | 'module'>) {
  const handleError = useThrowAsyncError();

  const [favoriteIds, setFavoriteIds] = useState<string[]>([]);

  useEffect(() => {
    const fetchFavoriteWords = async () => {
      if (!module || !wordlist?.words?.length) return;

      try {
        const { data } = await getFavoriteWordsForModule(module.id);

        setFavoriteIds(data);
      } catch (e) {
        handleError(e);
      }
    };

    fetchFavoriteWords();
  }, [handleError, module, wordlist]);

  const clearFavoriteWords = useCallback(async () => {
    if (!module) return;

    try {
      await deleteFavoriteWords(module.id, favoriteIds);
      setFavoriteIds([]);
      setSelectedWord(undefined);
    } catch (e) {
      handleError(e);
    }
  }, [favoriteIds, handleError, module, setSelectedWord]);

  const setNextSelectedWord = (unmarkedWordId: string) => {
    if (!wordlist || !selectedWord) return;

    const { words } = wordlist;

    const favoritedWords = words.filter(w => favoriteIds.includes(w.id));
    const wordIndex = favoritedWords.findIndex(word => word.id === unmarkedWordId);

    if (unmarkedWordId !== selectedWord.id) return;

    if (wordIndex === 0) return setSelectedWord(favoritedWords[1]);

    if (wordIndex === favoritedWords.length - 1) return setSelectedWord(favoritedWords[favoritedWords.length - 2]);

    return setSelectedWord(favoritedWords[wordIndex - 1]);
  };

  const toggleFavoriteWord = async (id: string) => {
    if (!module) return;

    try {
      if (favoriteIds.includes(id)) {
        await deleteFavoriteWord(module.id, id);

        setFavoriteIds(favoriteIds.filter(favoriteId => favoriteId !== id));

        if (currentTab === WordlistTab.FAVORITES) {
          setNextSelectedWord(id);
        }
      } else {
        await putFavoriteWord(module.id, id);
        setFavoriteIds([...favoriteIds, id]);
      }
    } catch (e) {
      handleError(e);
    }
  };

  return {
    favoriteIds,
    clearFavoriteWords,
    toggleFavoriteWord,
  };
}
